import type { DirectorOperationsView, UpdateDirectorControlInput } from "@actale/contracts";
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError } from "./api.js";
import { formatUpdatedAt } from "./story-utils.js";
import { RoleRejectionEvidence } from "./RoleRejectionEvidence.js";

interface ControlForm {
  externalCallsEnabled: boolean;
  adminExternalEnabled: boolean;
  appExternalEnabled: boolean;
  appRolloutPercent: number;
  dailyExternalLimit: number | null;
  agentTurnTimeoutMs: number;
  agentSessionExternalCallLimit: number;
  agentTurnCostReserveUsdMicros: number;
  agentSessionCostLimitUsdMicros: number;
}

function formFrom(data: DirectorOperationsView): ControlForm {
  return {
    externalCallsEnabled: data.control.externalCallsEnabled,
    adminExternalEnabled: data.control.adminExternalEnabled,
    appExternalEnabled: data.control.appExternalEnabled,
    appRolloutPercent: data.control.appRolloutPercent,
    dailyExternalLimit: data.control.dailyExternalLimit,
    agentTurnTimeoutMs: data.control.agentTurnTimeoutMs,
    agentSessionExternalCallLimit: data.control.agentSessionExternalCallLimit,
    agentTurnCostReserveUsdMicros: data.control.agentTurnCostReserveUsdMicros,
    agentSessionCostLimitUsdMicros: data.control.agentSessionCostLimitUsdMicros,
  };
}

function Switch({
  checked,
  disabled = false,
  label,
  description,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  label: string;
  description: string;
  onChange: (checked: boolean) => void;
}): React.ReactNode {
  return (
    <label className={`director-switch ${disabled ? "disabled" : ""}`}>
      <span><strong>{label}</strong><small>{description}</small></span>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(event) => onChange(event.target.checked)} />
      <i aria-hidden="true" />
    </label>
  );
}

function money(micros: number): string {
  return `$${(micros / 1_000_000).toFixed(micros >= 10_000 ? 2 : 4)}`;
}

function duration(milliseconds: number): string {
  return milliseconds < 1_000 ? `${milliseconds} 毫秒` : `${(milliseconds / 1_000).toFixed(1)} 秒`;
}

const STATUS_LABELS: Record<string, string> = {
  STARTED: "进行中",
  SUCCESS: "成功",
  FALLBACK: "已回退",
  FAILED: "失败",
  SKIPPED: "已拦截",
};

const SCOPE_LABELS: Record<string, string> = {
  ADMIN_SIMULATION: "后台试玩或规则测试",
  APP_PLAYTHROUGH: "APP 故事",
  OPENING_PREPARATION: "开场准备",
};

const PROVIDER_LABELS: Record<string, string> = {
  OPENAI_COMPATIBLE: "真实 AI",
  DETERMINISTIC: "备用导演",
};

const EXECUTION_STATUS_LABELS: Record<string, string> = {
  RESERVED: "等待导演",
  PLAN_READY: "导演已规划",
  COMPLETED: "已完成",
  FAILED: "已失败",
  RECOVERED: "已回收",
};

export function DirectorOperationsPage(): React.ReactNode {
  const [data, setData] = useState<DirectorOperationsView | null>(null);
  const [form, setForm] = useState<ControlForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [recoveringId, setRecoveringId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await api.directorOperations();
      setData(result);
      setForm(formFrom(result));
      setConfirmed(false);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "无法读取真实 AI 状态");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const activationRisk = useMemo(() => Boolean(
    data && form && (
      (!data.control.externalCallsEnabled && form.externalCallsEnabled)
      || (!data.control.appExternalEnabled && form.appExternalEnabled)
      || (
        data.control.dailyExternalLimit !== null
        && (form.dailyExternalLimit === null || form.dailyExternalLimit > data.control.dailyExternalLimit)
      )
      || form.agentTurnTimeoutMs > data.control.agentTurnTimeoutMs
      || form.agentSessionExternalCallLimit > data.control.agentSessionExternalCallLimit
      || form.agentTurnCostReserveUsdMicros > data.control.agentTurnCostReserveUsdMicros
      || form.agentSessionCostLimitUsdMicros > data.control.agentSessionCostLimitUsdMicros
    )
  ), [data, form]);

  async function apply(next: ControlForm, successMessage: string): Promise<void> {
    if (!data) return;
    setSaving(true);
    setError("");
    setNotice("");
    const input: UpdateDirectorControlInput = {
      ...next,
      expectedUpdatedAt: data.control.updatedAt,
    };
    try {
      const result = await api.updateDirectorOperations(input);
      setData(result);
      setForm(formFrom(result));
      setConfirmed(false);
      setNotice(successMessage);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "真实 AI 设置保存失败");
    } finally {
      setSaving(false);
    }
  }

  async function save(): Promise<void> {
    if (!form) return;
    if (form.appExternalEnabled && !form.adminExternalEnabled) {
      setError("允许 APP 使用真实 AI 前，必须先在后台试玩或规则测试中完成验证");
      return;
    }
    if (form.agentTurnCostReserveUsdMicros > form.agentSessionCostLimitUsdMicros) {
      setError("V2 单回合费用预留不能超过单局费用上限");
      return;
    }
    await apply(form, "真实 AI 设置已保存并写入审计日志。");
  }

  async function recover(executionId: string, expectedUpdatedAt: string): Promise<void> {
    if (!window.confirm("确认回收这个已超时的 V2 回合？系统不会重试或再次调用模型，只会把它标记为已回收并释放未使用的预留额度。")) return;
    setRecoveringId(executionId);
    setError("");
    setNotice("");
    try {
      await api.recoverDirectorTurnExecution(executionId, { expectedUpdatedAt });
      setNotice("已回收超时的 V2 回合；系统没有重试模型调用。");
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "V2 回合回收失败");
    } finally {
      setRecoveringId(null);
    }
  }

  if (loading && !data) {
    return <div className="director-loading"><span /><strong>正在读取真实 AI 状态…</strong></div>;
  }

  if (!data || !form) {
    return <div className="state-card"><div className="error-notice" role="alert"><span>!</span>{error || "真实 AI 状态不可用"}</div><button className="secondary-button" onClick={() => void load()}>重新加载</button></div>;
  }

  const today = data.today;
  const providerReady = data.provider.configured;

  return (
    <div className="director-operations">
      <div className="page-heading director-heading">
        <div>
          <p className="eyebrow">真实 AI 管理</p>
          <h1>真实 AI 管理</h1>
          <p>查看真实 AI 是否开启、哪些入口可以使用，以及今天的用量和费用。</p>
        </div>
        <button className="secondary-button" disabled={loading || saving} onClick={() => void load()}>{loading ? "正在刷新…" : "刷新数据"}</button>
      </div>

      {error ? <div className="error-notice" role="alert"><span>!</span>{error}</div> : null}
      {notice ? <div className="director-notice" role="status">✓ {notice}</div> : null}

      <section className={`director-provider-banner ${providerReady ? "ready" : "not-ready"}`}>
        <span className="director-provider-icon" aria-hidden="true">AI</span>
        <div>
          <strong>{providerReady ? "故事导演的真实 AI 已设置" : "故事导演的真实 AI 尚未设置"}</strong>
          <p>{providerReady ? `${data.provider.modelName ?? "未命名模型"}${data.provider.reasoningEffort ? ` · 思考深度 ${data.provider.reasoningEffort}` : ""}` : "当前只会使用备用导演，不会请求外部 AI 服务。"}</p>
        </div>
        <dl>
          <div><dt>最长等待</dt><dd>{data.provider.timeoutMs / 1_000} 秒</dd></div>
          <div><dt>AI 失败时</dt><dd>{data.provider.fallbackEnabled ? "备用导演继续" : "本轮报错"}</dd></div>
          <div><dt>费用计算</dt><dd>{data.provider.pricingConfigured ? "可用" : "未设置"}</dd></div>
        </dl>
      </section>

      <section className="director-metrics" aria-label="今日 AI 调用指标">
        <article><span>今天尝试使用真实 AI</span><strong>{today.externalAttempts}</strong><small>共 {today.total} 轮故事回应</small></article>
        <article><span>真实 AI 成功 / 备用导演</span><strong>{today.success} / {today.fallback}</strong><small>错误 {today.failed} · 未调用 {today.skipped}</small></article>
        <article><span>平均等待</span><strong>{duration(today.averageLatencyMs)}</strong><small>过去 7 天 {duration(data.last7Days.averageLatencyMs)}</small></article>
        <article><span>模型用量 / 估算费用</span><strong>{today.totalTokens.toLocaleString()}</strong><small>{data.provider.pricingConfigured ? money(today.estimatedCostUsdMicros) : "设置参考价后显示费用"}</small></article>
      </section>

      <div className="director-grid">
        <section className="director-control-panel">
          <header>
            <div><span className={form.externalCallsEnabled ? "live" : "safe"} /><div><strong>真实 AI 使用范围</strong><small>保存后，正在进行的故事会从下一轮开始使用新设置</small></div></div>
            {form.externalCallsEnabled ? <button className="danger-button" disabled={saving} onClick={() => void apply({ ...formFrom(data), externalCallsEnabled: false }, "已停用所有真实 AI。")}>停用所有真实 AI</button> : <span className="director-safe-badge">真实 AI 已停用</span>}
          </header>

          <div className="director-control-list">
            <Switch
              checked={form.externalCallsEnabled}
              disabled={!providerReady && !form.externalCallsEnabled}
              label="允许使用真实 AI"
              description="关闭后，后台和 APP 都只使用备用导演"
              onChange={(checked) => setForm({ ...form, externalCallsEnabled: checked })}
            />
            <Switch
              checked={form.adminExternalEnabled}
              disabled={!providerReady}
              label="后台试玩和规则测试使用真实 AI"
              description="用于内部试玩和故事规则测试，不影响正式 APP"
              onChange={(checked) => setForm({ ...form, adminExternalEnabled: checked, ...(checked ? {} : { appExternalEnabled: false }) })}
            />
            <Switch
              checked={form.appExternalEnabled}
              disabled={!providerReady || !form.adminExternalEnabled}
              label="APP 用户使用真实 AI"
              description="只对下方开放比例内的用户生效，同一场故事不会中途切换"
              onChange={(checked) => setForm({ ...form, appExternalEnabled: checked })}
            />
          </div>

          <div className="director-number-controls">
            <label>
              <span><strong>APP 开放比例</strong><small>{form.appRolloutPercent}% 用户</small></span>
              <input type="range" min="0" max="100" step="1" value={form.appRolloutPercent} onChange={(event) => setForm({ ...form, appRolloutPercent: Number(event.target.value) })} />
            </label>
            <label>
              <span><strong>每天最多使用次数</strong><small>{form.dailyExternalLimit === null ? "不限次数" : "每天零点重新计算（上海时间）"}</small></span>
              <input
                type="number"
                min="1"
                max="100000"
                value={form.dailyExternalLimit ?? ""}
                placeholder="留空表示不限次数"
                onChange={(event) => setForm({
                  ...form,
                  dailyExternalLimit: event.target.value === "" ? null : Number(event.target.value),
                })}
              />
            </label>
            <label>
              <span><strong>V2 整回合最长等待</strong><small>全局导演和角色演绎合计</small></span>
              <input type="number" min="30" max="240" value={form.agentTurnTimeoutMs / 1_000} onChange={(event) => setForm({ ...form, agentTurnTimeoutMs: Number(event.target.value) * 1_000 })} />
            </label>
            <label>
              <span><strong>V2 单局最多真实 AI 调用</strong><small>每个完整玩家回合预留 2 次</small></span>
              <input type="number" min="2" max="1000" value={form.agentSessionExternalCallLimit} onChange={(event) => setForm({ ...form, agentSessionExternalCallLimit: Number(event.target.value) })} />
            </label>
            <label>
              <span><strong>V2 单回合费用预留</strong><small>美元；结算后释放未使用部分</small></span>
              <input type="number" min="0.001" max="10" step="0.001" value={form.agentTurnCostReserveUsdMicros / 1_000_000} onChange={(event) => setForm({ ...form, agentTurnCostReserveUsdMicros: Math.round(Number(event.target.value) * 1_000_000) })} />
            </label>
            <label>
              <span><strong>V2 单局费用预算阈值</strong><small>按回合预留判断；与每天不限次数相互独立</small></span>
              <input type="number" min="0.001" max="100" step="0.01" value={form.agentSessionCostLimitUsdMicros / 1_000_000} onChange={(event) => setForm({ ...form, agentSessionCostLimitUsdMicros: Math.round(Number(event.target.value) * 1_000_000) })} />
            </label>
          </div>

          {activationRisk ? (
            <label className="director-confirm">
              <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />
              <span>我确认本次保存会扩大真实 AI 的使用范围或移除调用次数上限，并可能产生费用。</span>
            </label>
          ) : null}

          <footer>
            <span>最近更新：{formatUpdatedAt(data.control.updatedAt)} · {data.control.updatedBy?.displayName ?? "系统"}</span>
            <button className="primary-button compact" disabled={saving || (activationRisk && !confirmed)} onClick={() => void save()}>{saving ? "正在保存…" : "保存配置"}</button>
          </footer>
        </section>

        <section className="director-guard-panel">
          <header><strong>当前运行状态</strong><span>实时</span></header>
          <ul>
            <li><span className="ok">✓</span><div><strong>{form.externalCallsEnabled ? "真实 AI 已开启" : "真实 AI 已停用"}</strong><small>{form.externalCallsEnabled ? "只对上方允许的入口生效" : "后台和 APP 都不会使用真实 AI"}</small></div></li>
            <li><span className={form.dailyExternalLimit === null || today.externalAttempts < form.dailyExternalLimit ? "ok" : "warn"}>{form.dailyExternalLimit === null || today.externalAttempts < form.dailyExternalLimit ? "✓" : "!"}</span><div><strong>今天已用次数</strong><small>{form.dailyExternalLimit === null ? `${today.externalAttempts} 次 · 不限` : `${today.externalAttempts} / ${form.dailyExternalLimit} 次`}</small></div></li>
            <li><span className={data.provider.fallbackEnabled ? "ok" : "warn"}>{data.provider.fallbackEnabled ? "✓" : "!"}</span><div><strong>AI 失败时自动继续</strong><small>{data.provider.fallbackEnabled ? "使用备用导演继续故事" : "本轮会直接报错"}</small></div></li>
            <li><span className={data.last7Days.failed === 0 ? "ok" : "warn"}>{data.last7Days.failed === 0 ? "✓" : "!"}</span><div><strong>近 7 天运行情况</strong><small>{data.last7Days.failed} 次错误 · {data.last7Days.fallback} 次使用备用导演</small></div></li>
            <li><span className="ok">✓</span><div><strong>V2 双阶段完整性</strong><small>每回合先预留 2 次调用，总等待不超过 {form.agentTurnTimeoutMs / 1_000} 秒</small></div></li>
            <li><span className="ok">✓</span><div><strong>V2 单局预算</strong><small>最多 {form.agentSessionExternalCallLimit} 次调用 · 费用阈值 {money(form.agentSessionCostLimitUsdMicros)}</small></div></li>
          </ul>
        </section>
      </div>

      <section className="director-recent">
        <header><div><strong>最近 V2 回合</strong><small>超时回收不会重试，也不会产生新的模型调用</small></div><span>{data.recentExecutions.length} 条</span></header>
        <div className="table-shell">
          <table>
            <thead><tr><th>时间</th><th>故事现场</th><th>状态</th><th>回合</th><th>调用预留 / 结算</th><th>费用预留 / 结算</th><th>截止时间</th><th>操作</th></tr></thead>
            <tbody>
              {data.recentExecutions.map((item) => (
                <tr key={item.id}>
                  <td>{formatUpdatedAt(item.createdAt)}</td>
                  <td><strong>{SCOPE_LABELS[item.scope]}</strong><small>{item.targetId.slice(0, 8)}</small></td>
                  <td><span className={`director-status ${item.status.toLowerCase()}`}>{EXECUTION_STATUS_LABELS[item.status]}</span><small>{item.errorCode ?? "—"}</small></td>
                  <td>玩家 {item.turnSequence}<small>导演 {item.directorSequence}</small></td>
                  <td>{item.externalCallsReserved} / {item.externalCallsSettled}</td>
                  <td>{money(item.costReservedUsdMicros)} / {money(item.costSettledUsdMicros)}</td>
                  <td>{formatUpdatedAt(item.deadlineAt)}</td>
                  <td>{item.recoverable ? <button className="secondary-button compact" disabled={recoveringId === item.id} onClick={() => void recover(item.id, item.updatedAt)}>{recoveringId === item.id ? "回收中…" : "回收超时回合"}</button> : "—"}</td>
                </tr>
              ))}
              {!data.recentExecutions.length ? <tr><td colSpan={8} className="empty-table-cell">尚无 V2 双阶段回合记录。</td></tr> : null}
            </tbody>
          </table>
        </div>
      </section>

      <section className="director-recent">
        <header><div><strong>最近使用记录</strong><small>不保存完整请求；灯塔后台角色内容检查失败时，加密保存被拦回复，仅超级管理员可查看</small></div><span>{data.recent.length} 条</span></header>
        <div className="table-shell">
          <table>
            <thead><tr><th>时间</th><th>使用入口</th><th>阶段</th><th>使用方式</th><th>结果</th><th>等待时间</th><th>模型用量</th><th>费用 / 原因</th></tr></thead>
            <tbody>
              {data.recent.map((item) => (
                <tr key={item.id}>
                  <td>{formatUpdatedAt(item.createdAt)}</td>
                  <td><strong>{SCOPE_LABELS[item.scope]}</strong><small>{item.targetId.slice(0, 8)}</small></td>
                  <td>{item.stage === "DIRECTOR_PLAN" ? "全局导演" : item.stage === "ROLE_PERFORMANCE" ? "角色演绎" : "历史单阶段"}<small>{item.turnSequence === null ? "—" : `玩家回合 ${item.turnSequence}`}</small></td>
                  <td>{PROVIDER_LABELS[item.actualProvider ?? item.requestedProvider] ?? item.actualProvider ?? item.requestedProvider}<small>{item.modelName}</small></td>
                  <td><span className={`director-status ${item.status.toLowerCase()}`}>{STATUS_LABELS[item.status]}</span>{item.providerRespondedAt ? <small>模型已返回{item.status === "FAILED" ? "，后续处理未完成" : ""}</small> : null}</td>
                  <td>{item.latencyMs == null ? "—" : duration(item.latencyMs)}{item.providerLatencyMs != null ? <small>模型 {duration(item.providerLatencyMs)} · 后续处理 {duration(item.validationMs ?? 0)}</small> : null}</td>
                  <td>{item.totalTokens?.toLocaleString() ?? "—"}</td>
                  <td>{item.errorCode}<small>{item.estimatedCostUsdMicros == null ? "费用未知" : money(item.estimatedCostUsdMicros)}</small>{item.status === "FAILED" && item.diagnostic ? <details><summary>脱敏诊断</summary><pre>{JSON.stringify(item.diagnostic, null, 2)}</pre></details> : null}{item.rejectionEvidenceAvailable ? <RoleRejectionEvidence invocationId={item.id} isDirector={item.stage === "DIRECTOR_PLAN"} /> : null}</td>
                </tr>
              ))}
              {!data.recent.length ? <tr><td colSpan={8} className="empty-table-cell">尚无导演调用记录；确定性和真实模型调用都会从现在开始记录。</td></tr> : null}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
